import { Inject, Injectable, Injector } from '@angular/core';
import { assert } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { lastValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { shapeTypeToToolName } from '../../cross-team-shared/annotation/shared/abstract-tool';
import { KeyboardShortcutService } from '../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { ToolsHelperService } from '../../cross-team-shared/misc-services/tools-helper.service';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../team-apis/core/shell-connector';
import { ShapeType } from '../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentStore2D } from './state/abstract-sensor-content-store-2d';
import { AbstractShapeTool2D } from './tools/shared/abstract-shape-tool-2d';
import { ToolManager2DService } from './tools/shared/tool-manager-2d.service';

@Injectable()
export class RedrawService extends InjectableSuperclass {
  constructor(
    private store: AbstractSensorContentStore2D,
    @Inject(shellConnectorToken)
    private shellConnector: ShellConnector,
    private injector: Injector,
    keyboardShortcutService: KeyboardShortcutService,
    private toolsHelperService: ToolsHelperService,
  ) {
    super();
    this.subscribeTo(
      keyboardShortcutService
        .getMatchingEvent$('redraw')
        .pipe(filter(() => this.canRedraw())),
      async ({ keyboardEvent }) => {
        keyboardEvent.preventDefault();
        await this.redraw();
      },
    );
  }

  async redraw() {
    const focusedShapeStore = this.store.getFocusedShapeStore();
    assert(
      focusedShapeStore,
      'getFocusedShapeStore() always exist when redraw() is called',
    );
    const oldToolName = this.getCurrentToolName();
    const shapeType = focusedShapeStore.state().type;

    const releaseInteractionMode =
      this.shellConnector.restrictInteractions('canvas');
    const tool = this.getShapeTool(shapeType);
    this.store.batch(() => {
      tool.startRedraw(focusedShapeStore);
      this.store('selectedTool').set(tool.name);
    });

    await lastValueFrom(tool.drawShapeFinished$.pipe(take(1)));

    releaseInteractionMode();
    this.store('selectedTool').set(oldToolName);
  }

  canRedraw() {
    if (this.getCurrentToolName() !== 'Edit' || this.store.state().readOnly) {
      return false;
    }
    const focusedShapeStore = this.store.getFocusedShapeStore();
    if (!focusedShapeStore) {
      return false;
    }
    const shapeType = focusedShapeStore.state().type;
    const toolName = shapeTypeToToolName(shapeType);

    return this.toolsHelperService
      .getAllowedTools(this.store.state().output.name)
      .includes(toolName);
  }

  private getShapeTool(shapeType: ShapeType) {
    const tool = this.getToolManager().getTool(shapeTypeToToolName(shapeType));
    assert(
      tool instanceof AbstractShapeTool2D,
      'Only pass a shapeType that has a corresponding tool',
    );
    return tool;
  }

  private getCurrentToolName() {
    return this.getToolManager().getSelectedTool()?.name;
  }

  // ToolManagerService is not initialized when content test context is
  // initialized
  private getToolManager() {
    return this.injector.get(ToolManager2DService);
  }
}
